import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { Zap, Timer, UserCheck, Moon, Cpu, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { CountUp } from "./CountUp";
import { itemVariants } from "./motion";
import type { AutomationStats } from "../mockAutomations";
import { cn } from "@/lib/utils";

interface Tile {
  label: string;
  value: number;
  suffix?: string;
  decimals?: number;
  icon: ReactNode;
  tone: "primary" | "info" | "warning" | "accent";
  badge: string;
  sparkline: string;
}

const TONE_CONFIG: Record<
  Tile["tone"],
  {
    iconBg: string;
    iconText: string;
    badgeClass: string;
    strokeColor: string;
  }
> = {
  primary: {
    iconBg: "bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400",
    iconText: "text-blue-600 dark:text-blue-400",
    badgeClass: "bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200/80 dark:border-blue-800/80",
    strokeColor: "#3b82f6",
  },
  info: {
    iconBg: "bg-sky-50 dark:bg-sky-950/50 text-sky-600 dark:text-sky-400",
    iconText: "text-sky-600 dark:text-sky-400",
    badgeClass: "bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300 border-sky-200/80 dark:border-sky-800/80",
    strokeColor: "#0ea5e9",
  },
  warning: {
    iconBg: "bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400",
    iconText: "text-amber-600 dark:text-amber-400",
    badgeClass: "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/80",
    strokeColor: "#f59e0b",
  },
  accent: {
    iconBg: "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400",
    iconText: "text-indigo-600 dark:text-indigo-400",
    badgeClass: "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border-indigo-200/80 dark:border-indigo-800/80",
    strokeColor: "#6366f1",
  },
};

export function StatsStrip({ stats }: { stats: AutomationStats }) {
  const tiles: Tile[] = [
    {
      label: "AUTO-HANDLED",
      value: stats.autoHandledPct,
      suffix: "%",
      icon: <Zap className="h-4 w-4" />,
      tone: "primary",
      badge: "AI Resolved",
      sparkline: "M0,18 C15,12 30,22 45,10 C60,4 75,14 90,6 C105,2 120,10 130,4",
    },
    {
      label: "AVG AI RESPONSE",
      value: stats.avgResponseTimeSec,
      suffix: "s",
      decimals: 1,
      icon: <Timer className="h-4 w-4" />,
      tone: "info",
      badge: "Instant",
      sparkline: "M0,20 C20,16 35,8 55,14 C75,6 95,12 110,4 125,8 130,2",
    },
    {
      label: "ESCALATED TO HUMAN",
      value: stats.escalationsToHuman,
      icon: <UserCheck className="h-4 w-4" />,
      tone: "warning",
      badge: "Low Load",
      sparkline: "M0,16 C20,18 40,10 60,12 C80,6 100,8 120,2 128,4 130,1",
    },
    {
      label: "AFTER-HOURS HANDLED",
      value: stats.afterHoursAutoAnswered,
      icon: <Moon className="h-4 w-4" />,
      tone: "accent",
      badge: "24/7 Cover",
      sparkline: "M0,6 C20,10 40,8 60,16 C80,12 100,18 120,10 128,8 130,6",
    },
  ];

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
      {/* Pod Header */}
      <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5 gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/20 text-primary">
            <Cpu className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Automation Telemetry
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Active Engine
          </span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Live conversation metrics</span>
      </div>

      {/* 4 Telemetry Metric Capsules */}
      <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 lg:grid-cols-4 bg-white/70 dark:bg-slate-950/40">
        {tiles.map((tile) => {
          const cfg = TONE_CONFIG[tile.tone];
          return (
            <motion.div key={tile.label} variants={itemVariants} className="w-full">
              <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:border-primary/50 hover:shadow-xs cursor-pointer w-full">
                {/* Left Icon Pill */}
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200 group-hover/tile:scale-105",
                    cfg.iconBg
                  )}
                >
                  {tile.icon}
                </div>

                {/* Right Metric Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-1">
                    <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight">
                      <CountUp value={tile.value} suffix={tile.suffix} decimals={tile.decimals} />
                    </span>
                    <span
                      className={cn(
                        "inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.2 text-[8px] font-bold shadow-2xs whitespace-nowrap",
                        cfg.badgeClass
                      )}
                    >
                      {tile.badge}
                    </span>
                  </div>
                  <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 dark:group-hover/tile:border-slate-400 transition-colors whitespace-nowrap">
                    {tile.label}
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </Card>
  );
}

